import { useDispatch, useSelector } from "react-redux";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Button,
    Header,
    Input,
    Modal,
    Surface,
    styles,
    Grid,
    Text,
    Row,
    Avatar,
    Switch
} from "../../tmwx-design-web/src";
import { useTranslation } from "react-i18next";
import { checkFieldError, isEmpty } from "../../gui-common/appHelper";
import { RootState } from "../../gui-common/redux/store";
import { RESPONSE_CODES, ResponseBase } from "../../gui-common/redux/data";
import { getFieldErrorText } from "../../utils/helper";
import { AssignFirmwareRequest, Firmware, initialAssignFirmwareRequest, isAssignFirmwareRequestValid } from "gui-common/redux/data/firmware";
import { assignATRFirmwareToMachine, clearATRFrimwareError, clearATRFrimwareResponse } from "gui-common/redux/action/efe/efeFirmwareAction";
import {Device} from "gui-common/redux/data/device";
import {useVirtualizer} from "@tanstack/react-virtual";

interface AssignFirmwareProps {
    firmware: Firmware | null;
    open: boolean
    onClose: () => void
}

const AssignFirmware: React.FC<AssignFirmwareProps> = ({ open, firmware, onClose = () => { } }) => {
    const { t } = useTranslation()
    const dispatch: any = useDispatch();
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.atrFirmware.error);
    const response = useSelector<RootState, ResponseBase | null>(state => state.atrFirmware.response);
    const responseType = useSelector<RootState, 'assign' | 'upload' | null>(state => state.atrFirmware.responseType);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [request, setRequest] = useState<AssignFirmwareRequest>(initialAssignFirmwareRequest);
    const efeList = useSelector<RootState, Device[]>(state => state.device.deviceList);
    const efeListRef = useRef<HTMLDivElement>(null)
    const [efeFilter, setEfeFilter] = useState<string>('')
    const [efeListHeight, setEfeListHeight] = useState< 200 | 300 | 500 | 650>(300)
    const filteredEfeList = useMemo(() => {
        const filter = efeFilter.trim().toLowerCase();
        return efeList.filter(efe => efe.systemId.toLowerCase().includes(filter));
    }, [efeList, efeFilter]);

    const virtualizerEfe = useVirtualizer({
        count: filteredEfeList.length,
        getScrollElement: () => efeListRef.current,
        getItemKey: index => filteredEfeList[index].systemId,
        estimateSize: () => 44,
        overscan: 30
    })

    const virtualizedEfeList = virtualizerEfe.getVirtualItems()

    useEffect(() => {
        virtualizerEfe.scrollToOffset(0);
    }, [efeFilter, virtualizerEfe]);

    const handleSelectSystemId = (systemId: string) => {
        setRequest({...request, systemId: systemId})
    }

    const handleClear = () => {
        setRequest(initialAssignFirmwareRequest)
        dispatch(clearATRFrimwareResponse())
        dispatch(clearATRFrimwareError())
        onClose()
    }

    const handleFirmwareClick = () => {
        setValidationMessage('')
        dispatch(clearATRFrimwareResponse())
        dispatch(clearATRFrimwareError())
        if (isAssignFirmwareRequestValid(request)) {
            dispatch(assignATRFirmwareToMachine(request));
        } else {
            setValidationMessage(t(isEmpty(request.systemId) ? 'efe.noEfeSelected' : 'mandatoryFields'));
        }
    }

    const isHardwareTypeMismatch = (): boolean => {
        if (serverError?.errorList) {
            for (let i of serverError?.errorList) {
                if (i.errorCode === RESPONSE_CODES.HARDWARE_TYPE_MISMATCH) {
                    return true
                }
            }
        }

        return false
    }

    /* HANDLE SELECTED FIRMWARE */
    useEffect(() => {
        if (firmware) {
            setRequest({ ...request, firmwareId: firmware.id })
        }
    }, [firmware])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClear()
            }
        }
    }, [response])

    return (
        <Modal
            visible={open}
            closeModal={handleClear}
            type="dialog"
            dialogMaxWidth="m"
            contentElevation={0}
            header={
                <Header
                    title={t('efe.assign')}
                    subtitle={firmware?.filename}
                    actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost' onPress={onClose} />}
                />
            }
        >
            <Surface elevation={0}>
                <Grid.Container centered maxWidth="xl">
                    <Grid.Col m={12} lg={12} xl={12}>

                        {(serverError) && (
                            <div>
                                {isHardwareTypeMismatch() ? (
                                    <Alert style={{marginBottom: 8}} status="danger"
                                           text={t('efe.invalidHardwareType')}/>
                                ) : (
                                    <Alert style={{marginBottom: 8}} status="danger" text={t('errorOccurred')}/>
                                )}
                            </div>

                        )}

                        {!isEmpty(validationMessage) && (
                            <Alert style={{marginBottom: 8}} text={validationMessage} status="danger"/>
                        )}

                        <Input
                            style={styles.marginTop8}
                            fullWidth
                            error={checkFieldError('effectiveDate', serverError)}
                            icon={'schedule_outlined'}
                            placeholder={t('atr.firmware.effectiveDate')}
                            value={request.effectiveDate}
                            variant='datetime'
                            onChangeValue={(text) => setRequest({...request, effectiveDate: text})}
                            helperText={getFieldErrorText('effectiveDate', serverError, t)}
                            dateLocale="hu-HU"
                        />
                    </Grid.Col>

                    <Grid.Col style={{padding: 0}}>
                        <Text bold style={styles.marginTop16}>
                            {t('efe.id')}
                        </Text>

                        <Surface style={{...styles.marginTop4, ...{padding: 4, paddingBottom: 0}}}>
                            <div>
                                <Input
                                    style={styles.marginTop8}
                                    fullWidth
                                    icon={"search_outlined"}
                                    placeholder={t('search')}
                                    value={efeFilter}
                                    onChangeValue={(text) => setEfeFilter(text)}
                                />
                                {filteredEfeList.length === 0 && <Text>{t('noData')}</Text>}
                                <div ref={efeListRef}
                                     style={{...styles.width100p, ...{height: efeListHeight, overflow: 'auto'}}}>
                                    <div
                                        style={{
                                            height: `${virtualizerEfe.getTotalSize()}px`,
                                            width: '100%',
                                            position: 'relative'
                                        }}
                                    >
                                        {virtualizedEfeList.map((virtualItem) => {
                                            const efe = filteredEfeList[virtualItem.index];
                                            return <div
                                                key={virtualItem.key}
                                                data-index={virtualItem.index}
                                                ref={virtualizerEfe.measureElement}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    transform: `translateY(${virtualItem.start}px)`
                                                }}
                                            >
                                                <Row
                                                    title={efe.systemId}
                                                    avatar={<Avatar size='small' iconName={'work_outlined'}
                                                        color={request.systemId === efe.systemId
                                                            ? 'primary' : 'neutral'}/>}
                                                    actionRight={<Switch
                                                        isEnabled={request.systemId === efe.systemId}
                                                        toggleSwitch={() => handleSelectSystemId(efe.systemId)}/>}
                                                    selected={request.systemId === efe.systemId}
                                                    touchable
                                                    touchableAction={() => handleSelectSystemId(efe.systemId)}
                                                />
                                            </div>;
                                        })}
                                    </div>
                                </div>
                            </div>
                        </Surface>
                    </Grid.Col>

                    <Grid.Col m={12} lg={12} xl={12}>
                        <div style={{...styles.marginTop8, ...styles.row, ...{justifyContent: 'flex-end'}}}>
                            <Button status="danger" variant="outlined" onPress={handleClear}>
                                {t('cancel')}
                            </Button>
                            <Button style={styles.marginLeft16} onPress={handleFirmwareClick}>
                                {t('add')}
                            </Button>
                        </div>
                    </Grid.Col>
                </Grid.Container>
            </Surface>
        </Modal>
    )
};

export default AssignFirmware;
